import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSavedSessions } from "../hooks/useSavedSessions";
import { FavoriteMetadata } from "../types/NameResult";
import { LuStar, LuHeart } from "react-icons/lu";

interface SharedFavoritesSession {
  favorites: string[];
  metadata: Record<string, FavoriteMetadata>;
  timestamp: number;
}

export default function ShareSessionPage() {
  const { id } = useParams();
  const { sessions } = useSavedSessions();
  const [session, setSession] = useState<null | typeof sessions[0]>(null);
  const [sharedFavorites, setSharedFavorites] = useState<SharedFavoritesSession | null>(null);

  useEffect(() => {
    if (id) {
      // Check if it's a saved session
      if (sessions.length > 0) {
        const match = sessions.find((s) => s.id === id);
        if (match) {
          setSession(match);
          return;
        }
      }

      // Check if it's a shared favorites session
      const allSessions = JSON.parse(localStorage.getItem('shared_sessions') || '{}');
      if (allSessions[id]) {
        setSharedFavorites(allSessions[id]);
      }
    }
  }, [id, sessions]);

  // Render shared favorites session
  if (sharedFavorites) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <LuHeart className="w-6 h-6 text-pink-600" />
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Shared Favorites
              </h1>
            </div>
            <p className="text-gray-600">
              Someone shared their favorite names with you. View and discuss them together!
            </p>
            <div className="mt-3 text-sm text-gray-500">
              Shared on: {new Date(sharedFavorites.timestamp).toLocaleDateString()}
            </div>
          </div>

          <div className="space-y-4">
            {sharedFavorites.favorites.map((name) => {
              const metadata = sharedFavorites.metadata[name];
              return (
                <div
                  key={name}
                  className={`bg-white border rounded-xl p-5 shadow-sm ${
                    metadata?.isShortlisted
                      ? 'border-pink-300 ring-2 ring-pink-100'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-gray-900">{name}</h3>
                    <div className="flex items-center gap-2">
                      {metadata?.isShortlisted && (
                        <span className="px-2 py-1 bg-pink-100 text-pink-700 text-xs font-medium rounded">
                          Shortlisted
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Voting */}
                  {metadata?.vote && (
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-sm text-gray-600">Rating:</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((stars) => (
                          <LuStar
                            key={stars}
                            className="w-4 h-4 text-yellow-500"
                            fill={metadata.vote && metadata.vote >= stars ? 'currentColor' : 'none'}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-500 ml-1">
                        ({metadata.vote} star{metadata.vote !== 1 ? 's' : ''})
                      </span>
                    </div>
                  )}

                  {/* Tags */}
                  {metadata?.tags && metadata.tags.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {metadata.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Notes */}
                  {metadata?.notes && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-sm text-gray-600">
                        <strong>Notes:</strong> {metadata.notes}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Session not found</h2>
          <p className="text-gray-600">This shared session may have expired or doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-bold mb-6">🔗 Shared Session</h1>

      <div className="text-sm text-gray-500 mb-4">
        <strong>Keywords:</strong> {session.keywords.join(", ")} | <strong>Tone:</strong> {session.tone} | <strong>Length:</strong> {session.length} | <strong>TLDs:</strong> {session.tlds?.join(', ') ?? ''}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {session.results.map((r) => (
          <div key={r.name} className="rounded-xl border border-gray-200 p-4 bg-white shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="text-lg font-semibold text-gray-900">{r.name}</div>
              <div>{r.available ? "✅" : "❌"}</div>
            </div>
            {/* {r.description && (
              <div className="text-sm text-gray-500 italic mb-2">{r.description}</div>
            )} */}
            {r.slogan && (
              <div className="text-sm text-gray-700">📝 {r.slogan}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
